import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { getIsLoggedInCheck } from "../../../services/isLoggedInCheck.service";
import { safeReadUsers, safeGetLoginAccessToken } from "../../../services/auth/loginRetry";
import { setIsAuthorized, setPassword } from "../../../redux/login/actions";
import { useCustomer } from "../../../Contexts/CustomerContext";
import CustomLoader from "../../Loader/Spinner";
import AuthenticatorDialog from "../../../containers/Authenticator/Authenticator/AuthenticatorDialog";
import CodeVerificationDialog from "../../../containers/Authenticator/CodeVerifiaction/CodeVerificationDialog";
import AuthLayout from "../AuthLayout";
import { authTokenStore } from "../../../services/auth/tokenStore";

// Assets
import WadeLogo from "../../../components/Icons/WadeLogo.svg";
import Group9 from "../../../assets/images/Group 9.png"; // Tick icon

// Icons
import Group11 from "../../../assets/images/svg/group11"; // Eye 
import Group7 from "../../../assets/images/svg/group7"; // Eye
import { setScopeCustomerId } from "../../../services/activeScope";

interface OTPInputProps {
  isLoggedInHandler: () => void;
}

const Login: React.FC<OTPInputProps> = ({ isLoggedInHandler }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { refreshCustomers } = useCustomer();

  // State
  const [login, setLogin] = useState({
    email: "",
    password: "",
  });

  // Validation State - Simplified
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Additional UI State
  const [showPassword, setShowPassword] = useState(false);

  // Dialogs & Loading
  const [authenticatorDialog, setAuthenticatorDialog] = useState(false);
  const [codeVerificationDialog, setCodeVerificationDialog] = useState(false);
  const [authMethod, setAuthMethod] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [userId, setUserId] = useState<any>(null);

  const persistAuthTokens = (tokenResponse: {
    access_token?: string | null;
    refresh_token?: string | null;
    user_id?: string | number | null;
  }) => {
    authTokenStore.setTokens({
      accessToken: tokenResponse.access_token ?? undefined,
      refreshToken: tokenResponse.refresh_token ?? undefined,
      userId:
        typeof tokenResponse.user_id === "number" || typeof tokenResponse.user_id === "string"
          ? String(tokenResponse.user_id)
          : tokenResponse.user_id === null
            ? null
            : undefined,
    });
  };

  const persistEmail = (emailValue: string) => {
    if (emailValue) {
      authTokenStore.setTokens({ email: emailValue });
    }
  };

  useEffect(() => {
    localStorage.removeItem("isLoggedIn");
  }, []);

  const validateEmail = (email: string) => {
    return /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(email);
  };

  const onDoneButtonClick = async () => {
    try {
      const response = await safeReadUsers(login.email, login.password);
      if (response.is_verified === false) {
        navigate("/");
        return;
      }

      dispatch(setIsAuthorized(true));
      localStorage.setItem("isLoggedIn", "true");
      isLoggedInHandler();
      if (authTokenStore.getAccessToken()) {
        try {
          await refreshCustomers();
        } catch (error) {
          console.error("Failed to refresh customers after login", error);
        }
      }
      navigate("/home");
    } catch (error) {
      console.error("Error in onDoneButtonClick", error);
      // Handle error appropriately
    }
  };

  const handleSuccessSubmit = async () => {
    setCodeVerificationDialog(false);
    setIsLoading(false);
    try {
      await onDoneButtonClick();
    } catch (error) {
      console.error("Failed to complete login after verification", error);
      navigate("/");
    }
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLogin({ ...login, [e.target.name]: e.target.value });
    // Reset errors on type
    if (hasError) {
      setHasError(false);
      setErrorMessage("");
    }
  };

  const loginClickHandler = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    setHasError(false);
    setErrorMessage("");

    if (!login.email || !login.password) {
      setHasError(true);
      setErrorMessage("Email or Password fields cannot be empty.");
      return;
    }

    if (!validateEmail(login.email)) {
      setHasError(true);
      setErrorMessage("Invalid Email format.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await safeReadUsers(login.email, login.password);

      if (response.logged_in_using_temporary_password) {
        navigate("/change_password", {
          state: {
            temporary_password: login.password,
            email: login.email,
          },
        });
        return;
      }

      // Customer Logic
      const customerIds: number[] = Array.isArray(response?.customer_ids)
        ? response.customer_ids
        : response?.customer_id
          ? [response.customer_id]
          : [];
      const currentCustomerId =
        typeof response?.current_customer_id === "number"
          ? response.current_customer_id
          : customerIds[0];

      if (customerIds.length) {
        localStorage.setItem("customer_ids", JSON.stringify(customerIds));
      } else {
        localStorage.removeItem("customer_ids");
      }

      if (typeof currentCustomerId === "number") {
        setScopeCustomerId(String(currentCustomerId));
      } else {
        setScopeCustomerId(null);
      }
      persistEmail(login.email);
      dispatch(setPassword(login.password));

      if (response.email === login.email) {
        setAuthMethod(response.auth_method);

        const isLoggedInCheckResponse = await getIsLoggedInCheck(
          response.user_id,
          login.email,
          login.password
        );

        setUserId(isLoggedInCheckResponse.user_id);
        if (isLoggedInCheckResponse.has_logged_in_using_totp) {
          // Needs verification
          try {
            const tokenRes = await safeGetLoginAccessToken(login.email, login.password);
            persistAuthTokens(tokenRes);
            setCodeVerificationDialog(true);
          } catch (tokenErr) {
            // handle error
            setHasError(true);
            setErrorMessage("Authentication failed.");
            setIsLoading(false);
          }
        } else {
          setAuthenticatorDialog(true);
        }
      }
    } catch (error) {
      console.error("Failed in loginClickHandler:", error);
      setIsLoading(false);
      setHasError(true);
      setErrorMessage("Invalid Email or Password. Please try again.");
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <AuthLayout>
      <div className="tw-w-full tw-max-w-md tw-flex tw-flex-col tw-items-center">
        {/* Logo */}
        <img src={WadeLogo} alt="WADE" className="tw-h-10 tw-mb-12" />

        <h2 className="tw-text-3xl tw-font-semibold tw-text-[#03234a] tw-mb-8">Login</h2>

        <form onSubmit={loginClickHandler} className="tw-w-full">
          {/* Email Input */}
          <div className="tw-mb-6 tw-relative">
            <label htmlFor="email" className="tw-block tw-text-[#03234a] tw-mb-2 tw-text-sm tw-font-medium">
              Email
            </label>
            <div className="tw-relative">
              <input
                id="email"
                name="email"
                type="email"
                placeholder="Type your email"
                value={login.email}
                onChange={onChange}
                disabled={isLoading}
                className={`tw-w-full tw-h-12 tw-px-4 tw-rounded-md tw-border tw-text-base focus:tw-outline-none focus:tw-ring-2 tw-transition-colors
                              ${hasError ? 'tw-border-[#e88181] focus:tw-ring-[#e88181]' : 'tw-border-gray-200 focus:tw-ring-blue-100 focus:tw-border-[#03234a]'}
                          `}
              />
              {validateEmail(login.email) && login.email !== "" && (
                <div className="tw-absolute tw-right-3 tw-top-1/2 tw-transform -tw-translate-y-1/2">
                  <img src={Group9} alt="Valid" className="tw-w-4 tw-h-4" />
                </div>
              )}
            </div>
          </div>

          {/* Password Input */}
          <div className="tw-mb-2 tw-relative">
            <label htmlFor="password" className="tw-block tw-text-[#03234a] tw-mb-2 tw-text-sm tw-font-medium">
              Password
            </label>
            <div className="tw-relative">
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                placeholder="Type your password"
                value={login.password}
                onChange={onChange}
                disabled={isLoading}
                maxLength={100}
                className={`tw-w-full tw-h-12 tw-px-4 tw-pr-12 tw-rounded-md tw-border tw-text-base focus:tw-outline-none focus:tw-ring-2 tw-transition-colors
                              ${hasError ? 'tw-border-[#e88181] focus:tw-ring-[#e88181]' : 'tw-border-gray-200 focus:tw-ring-blue-100 focus:tw-border-[#03234a]'}
                          `}
              />
              {login.password !== "" && (
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="tw-absolute tw-right-3 tw-top-1/2 tw-transform -tw-translate-y-1/2 tw-p-1 tw-text-gray-500 hover:tw-text-gray-700 focus:tw-outline-none"
                >
                  {showPassword ? <Group7 /> : <Group11 />}
                </button>
              )}
            </div>
          </div>

          {/* Error Message */}
          <div className="tw-h-6 tw-mb-2">
            {hasError && (
              <p className="tw-text-[#e88181] tw-text-sm">
                {errorMessage}
              </p>
            )}
          </div>

          {/* Forgot Password */}
          <div className="tw-flex tw-justify-start tw-mb-6">
            <button
              type="button"
              onClick={() => navigate("./forgot_password")}
              className="tw-bg-transparent tw-border-none tw-p-0 tw-text-[#03234a] tw-text-sm hover:tw-underline focus:tw-outline-none"
            >
              Forgot Password?
            </button>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="tw-w-full tw-h-12 tw-bg-[#03234a] tw-text-[#d0fb56] tw-font-bold tw-rounded-md hover:tw-bg-[#021833] focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-[#03234a] disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-all tw-shadow-md active:tw-transform active:tw-scale-[0.98] tw-flex tw-items-center tw-justify-center"
          >
            {isLoading ? <CustomLoader variant="blue" /> : "LOGIN"}
          </button>
        </form>

        {/* Footer */}
        <div className="tw-mt-auto tw-absolute tw-bottom-4 tw-text-xs tw-text-[#03234a] tw-flex tw-items-center">
          <span>©2025 All Rights Reserved</span>
          <span className="tw-mx-2">|</span>
          <a
            href="https://wadeinsight.com/privacy-policy/"
            target="_blank"
            rel="noreferrer"
            className="hover:tw-underline"
          >
            Privacy Policy
          </a>
        </div>
      </div>

      {/* Modals */}
      <AuthenticatorDialog
        show={authenticatorDialog}
        onHide={() => {
          setAuthenticatorDialog(false);
          setIsLoading(false);
        }}
        email={login.email}
        password={login.password}
        user_id={userId}
      />
      {codeVerificationDialog && (
        <CodeVerificationDialog
          show={codeVerificationDialog}
          handleSuccessSubmit={handleSuccessSubmit}
          email={login.email}
          password={login.password}
          user_id={userId}
          verifyType="existingUser"
          authMethod={authMethod}
          length={6}
        />
      )}
    </AuthLayout>
  );
};

export default Login;
